import React, { useContext, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import "./Approvals.css";
import SubHeader from "../Subheader/Subheader";
import OvertimeApproval from "./OvertimApproval/OvertimeApproval";
import DayoffApproval from "./DayoffApproval/DayoffApproval";
import ApprovedReports from "./ApprovedReports/ApprovedReports";
import TimesheetApproval from "./TimesheetApproval/TimesheetApproval";
import SalaryApproval from "./SalaryApproval/SalaryApproval";
import ProjectApproval from "./ProjectApproval/ProjectApproval";
import ProjectApprovalDetail from "./ProjectApproval/ProjectApprovalDetail";
import SketchDesignApproval from "./SketchDesignApproval/SketchDesignApproval";
import { UserContext } from "../../contexts/CurrentUserContext";
import Badge from "../Report/Badge/Badge";
import { usePendingReports } from "../../contexts/PendingReportsContext";
import { usePendingTimesheets } from "../../contexts/PendingTimesheetsContext";
import { usePendingSalaryApprovals } from "../../contexts/PendingSalaryApprovalsContext";

function Approvals() {
  const { userData } = useContext(UserContext);
  const { pendingOvertimesCount, pendingDayoffsCount, updatePendingCounts } =
    usePendingReports();
  const { pendingTimesheetsCount } = usePendingTimesheets();
  const { pendingSalaryApprovalsCount } = usePendingSalaryApprovals();

  const getLevelName = (level) =>
    typeof level === "string" ? level : level?.name;

  const hasAccessToOvertimeApproval = () => {
    const allowedLevels = ["АДМС", "РО", "НО", "РГ", "ГИП", "ИК"];
    return userData?.access_levels?.some((level) =>
      allowedLevels.includes(getLevelName(level))
    );
  };

  const hasAccessToTimesheetApproval = () => {
    const allowedLevels = ["АДМС"];
    return (
      userData?.access_levels?.some((level) =>
        allowedLevels.includes(getLevelName(level))
      ) ||
      userData?.id === 62 ||
      userData?.id === 131
    );
  };

  const hasAccessToSalaryApproval = () => {
    const allowedLevels = ["АДМС", "РО"];
    return userData?.access_levels?.some((level) =>
      allowedLevels.includes(getLevelName(level))
    );
  };

  const hasAccessToProjectApproval = () => {
    const allowedLevels = ["АДМС", "РО", "НО", "РГ", "ГИП", "ИК", "ФИН"];
    return userData?.access_levels?.some((level) =>
      allowedLevels.includes(getLevelName(level))
    );
  };

  useEffect(() => {
    const allowedLevels = ["АДМС", "РО", "НО", "РГ", "ГИП", "ИК"];
    const hasAccess = userData?.access_levels?.some((level) =>
      allowedLevels.includes(getLevelName(level))
    );
    if (hasAccess) {
      updatePendingCounts();
    }
  }, [userData, updatePendingCounts]);

  const tabs = [
    ...(hasAccessToOvertimeApproval()
      ? [
          {
            id: 1,
            label: (
              <div style={{ position: "relative", display: "inline-block" }}>
                СОГЛАСОВАНИЕ ПЕРЕРАБОТОК
                <Badge count={pendingOvertimesCount} />
              </div>
            ),
            path: "overtime-approval",
            Component: OvertimeApproval,
          },
          {
            id: 2,
            label: (
              <div style={{ position: "relative", display: "inline-block" }}>
                СОГЛАСОВАНИЕ ОТГУЛОВ
                <Badge count={pendingDayoffsCount} />
              </div>
            ),
            path: "dayoff-approval",
            Component: DayoffApproval,
          },
        ]
      : []),
    ...(hasAccessToTimesheetApproval()
      ? [
          {
            id: 3,
            label: (
              <div style={{ position: "relative", display: "inline-block" }}>
                СОГЛАСОВАНИЕ ТАБЕЛЕЙ
                {pendingTimesheetsCount > 0 && (
                  <Badge count={pendingTimesheetsCount} />
                )}
              </div>
            ),
            path: "timesheet-approval",
            Component: TimesheetApproval,
          },
        ]
      : []),
    ...(hasAccessToSalaryApproval()
      ? [
          {
            id: 4,
            label: (
              <div style={{ position: "relative", display: "inline-block" }}>
                СОГЛАСОВАНИЕ ЗАРПЛАТЫ
                {pendingSalaryApprovalsCount > 0 && (
                  <Badge count={pendingSalaryApprovalsCount} />
                )}
              </div>
            ),
            path: "salary-approval",
            Component: SalaryApproval,
          },
        ]
      : []),
    ...(hasAccessToOvertimeApproval()
      ? [
          {
            id: 5,
            label: "ИСТОРИЯ СОГЛАСОВАНИЙ",
            path: "approved-reports",
            Component: ApprovedReports,
          },
        ]
      : []),
    ...(hasAccessToProjectApproval()
      ? [
          {
            id: 6,
            label: "СОГЛАСОВАНИЕ ПРОЕКТА",
            path: "project-approval",
            Component: ProjectApproval,
          },
          {
            id: 7,
            label: "СОГЛАСОВАНИЕ ПРОЕКТОВ ЭП",
            path: "sketch-design-approval",
            Component: SketchDesignApproval,
          },
        ]
      : []),
  ];

  // Если нет доступа ни к одному согласованию, показываем сообщение
  if (tabs.length === 0) {
    return (
      <div className="approvals">
        <div className="approvals__no-access">
          <h2>Доступ запрещен</h2>
          <p>У вас нет прав для просмотра согласований.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="approvals">
      <SubHeader title="Согласования" tabs={tabs} />
      <Routes>
        {tabs.map(({ path, Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}
        <Route
          path="project-approval/:projectId/:approvalId"
          element={<ProjectApprovalDetail />}
        />
        <Route
          path=""
          element={
            <Navigate
              to={tabs.length > 0 ? tabs[0].path : "overtime-approval"}
              replace
            />
          }
        />
      </Routes>
    </div>
  );
}

export default Approvals;
